import { FeatureNameEnum } from 'constants-content';

const INTERACTION_DATE_FIELDS = [
  '_created',
  '_updated',
  'startDate',
  'endDate',
  'cancelDate',
] as const;

const DATE_RANGE_FIELDS = [
  'startDate',
  'endDate',
  'startTime',
  'endTime',
] as const;

const SCHEDULE_DATE_FIELDS = [
  ...DATE_RANGE_FIELDS,
  'endRepeatDate',
] as const;

type DateInput = string | Date | null | undefined;

function toDate(value: DateInput): Date | null | undefined {
  return typeof value === 'string' ? new Date(value) : value;
}

function withParsedFields<T extends object>(
  source: T | null | undefined,
  fields: readonly (keyof T)[]
): T | null | undefined {
  if (!source) {
    return source;
  }

  const parsed = { ...source };

  for (const field of fields) {
    parsed[field] = toDate(parsed[field] as DateInput) as T[keyof T];
  }

  return parsed;
}

type ScheduleFields = {
  startDate?: unknown;
  endDate?: unknown;
  startTime?: unknown;
  endTime?: unknown;
  endRepeatDate?: unknown;
  dates?: unknown;
};

function withParsedSchedule<T extends ScheduleFields>(
  schedule: T | null | undefined
): T | null | undefined {
  if (!schedule) {
    return schedule;
  }

  const parsed = withParsedFields(schedule, SCHEDULE_DATE_FIELDS);

  if (parsed && Array.isArray(parsed.dates)) {
    parsed.dates = parsed.dates.map(toDate);
  }

  return parsed;
}

/**
 * An interaction read back over GraphQL carries its dates as ISO strings,
 * because `features` is a JSON scalar and the date scalars are not parsed
 * client side. The renderer and the blocks below it assume the Date objects
 * the create path builds in memory — `useReservableInput` calls
 * `.toISOString()` on the saved reservation, for one. Parse once here so
 * the edit path hands down the same types creation does.
 */
export function parseInteractionDates(interaction: any) {
  if (!interaction) {
    return interaction;
  }

  const parsed = withParsedFields(interaction, INTERACTION_DATE_FIELDS);

  if (!parsed.features) {
    return parsed;
  }

  const reservable = parsed.features[FeatureNameEnum.Reservable];
  const visitorManagement = parsed.features[FeatureNameEnum.VisitorManagement];

  parsed.features = {
    ...parsed.features,
    ...(reservable && {
      [FeatureNameEnum.Reservable]: {
        ...reservable,
        reservation: withParsedFields(
          reservable.reservation,
          DATE_RANGE_FIELDS
        ),
      },
    }),
    ...(visitorManagement && {
      [FeatureNameEnum.VisitorManagement]: {
        ...visitorManagement,
        schedule: withParsedSchedule(visitorManagement.schedule),
      },
    }),
  };

  return parsed;
}
